import React from "react";
import { PDFViewer as PaperviewPDF } from "@paperviewpdf/react-viewer";
import {
  PdfAnnotationSubtype,
  PdfBlendMode,
  uuidV4,
} from "@paperviewpdf/models";
import security from "../../services/Security";
import { loadDocumentUrl } from "../../lib/axios-client";
import { waitForPdfReady } from "../../lib/pdfReadiness";
import Tooltip from "../Common/Tooltip";
import Spinner from "../Common/Spinner";

import GreenCheckmark from "../../images/icons/svg/green-checkmark.svg";
import Pencil from "../../images/icons/svg/pencil.svg";

/* UI Kit */
import { UikButton } from "@uik";
import "@uik/styles.css";
import styles from "./PDFTReference.module.scss";
import {
  PIN_TYPES,
  getPinTools,
  importPinXfdf,
  isPinAnnotation,
  registerPinCommands,
  togglePinTool,
  updatePinToolbar,
} from "../../pdf/pinDrop";
import { isShapeAnnotation } from "../../pdf/shapeAnnotations";
import { loadStoredZoomLevel, rememberZoomLevel } from "../../pdf/zoomLevel";

// The reference viewer is about making references, so it only offers the
// reference pin.
var REFERENCE_PIN_TYPES = [PIN_TYPES.REFERENCE];
var REFERENCE_PIN_TOOLS = getPinTools(REFERENCE_PIN_TYPES);

// Captions saved for references made with a pin or a shape: neither has
// selected text. "Pindrop Reference" is what Papercurve v2 saved for pins,
// and reference cards recognise it to show the pin icon.
var PIN_REFERENCE_CAPTION = "Pindrop Reference";
var SHAPE_REFERENCE_CAPTION = "Shape Reference";

// Paperview PDF category of the "Shapes" toolbar tab. Disabled in the
// viewer config, and enabled once the viewer is ready unless nothing may
// be drawn (approved or read-only documents).
var SHAPES_MODE_CATEGORY = "mode-shapes";

/**
 * Generates a human-readable annotation ID.
 * Format: d{docId}_u{userId}_p{pageIndex}_{random}
 */
function generateAnnotationId(docId, userId, pageIndex) {
  var rand = Math.random().toString(36).substr(2, 5);
  return "d" + docId + "_u" + userId + "_p" + pageIndex + "_" + rand;
}

/**
 * Converts a positive integer to a base-26 letter sequence.
 * 1 → "A", 2 → "B", ..., 26 → "Z", 27 → "AA", etc.
 * Used for auto-generating reference annotation labels (pg1 A, pg1 B, ...).
 */
function numberToEncodedLetter(number) {
  if (isNaN(number)) return undefined;
  number = Math.abs(Math.floor(number));

  var dictionary = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  var index = number % dictionary.length;
  var quotient = number / dictionary.length;
  var result;

  if (number <= dictionary.length) {
    return numToLetter(number);
  }

  if (quotient >= 1) {
    if (index === 0) quotient--;
    result = numberToEncodedLetter(quotient);
  }

  if (index === 0) index = dictionary.length;

  return result + numToLetter(index);

  function numToLetter(num) {
    if (num > dictionary.length || num < 0) return undefined;
    if (num === 0) return "A";
    return dictionary.slice(num - 1, num);
  }
}

/**
 * Dropdown menu for document actions: Download, Delete, Rename.
 * Rendered in the top-right corner of the viewer container.
 */
class Menu extends React.Component {
  state = {
    showDropdown: false,
  };

  onClick = () => {
    this.setState({
      showDropdown: !this.state.showDropdown,
      userRole: security.getUserRole(),
    });
  };

  render() {
    var color = "#ADB5BD";
    if (this.state.showDropdown) {
      color = "#1A7285";
    }
    return (
      <div>
        <div
          style={{
            color: color,
            cursor: "pointer",
            fontSize: "10px",
            marginRight: "4px",
            marginLeft: "4px",
          }}
          onClick={this.onClick}
        >
          &#9679;&#9679;&#9679;
        </div>
        {this.state.showDropdown && (
          <div
            style={{
              position: "absolute",
              top: "37px",
              right: 0,
              backgroundColor: "white",
              width: "192px",
              borderRadius: "4px",
              zIndex: "99",
            }}
          >
            <div
              style={{
                color: "#3e3f42",
                padding: "8px 16px",
                cursor: "pointer",
              }}
              onClick={function () {
                this.props.downloadDocument();
                this.setState({ showDropdown: false });
              }.bind(this)}
            >
              Download
            </div>
            {this.state.userRole !== "viewer" && (
              <div>
                <div
                  style={{
                    height: "1px",
                    background: "#EAEDF3",
                  }}
                ></div>
                <div
                  style={{
                    color: "#3e3f42",
                    padding: "8px 16px",
                    cursor: "pointer",
                  }}
                  onClick={function () {
                    this.props.showDeleteModal();
                    this.setState({ showDropdown: false });
                  }.bind(this)}
                >
                  Delete
                </div>
                <div
                  style={{
                    height: "1px",
                    background: "#EAEDF3",
                  }}
                ></div>
                <div
                  style={{
                    color: "#3e3f42",
                    padding: "8px 16px",
                    cursor: "pointer",
                  }}
                  onClick={function () {
                    this.props.showRenameModal();
                    this.setState({ showDropdown: false });
                  }.bind(this)}
                >
                  Rename
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
}

class PDFTReference extends React.Component {
  constructor(props) {
    super(props);

    // PaperView PDF plugin references
    this.annotationPlugin = null;
    this.selectionPlugin = null;
    this.xfdfPlugin = null;
    this.scrollPlugin = null;
    this.searchPlugin = null;
    this.commandsPlugin = null;
    this.uiPlugin = null;
    this.documentManagerPlugin = null;

    // Visibility toggle: IDs being hidden via toggle (guards event handlers)
    this.toggleHiddenAnnotationIds = {};
    // Snapshot of native annotations for re-import after hiding
    this.nativeAnnotationSnapshot = [];
    // IDs of reference annotations (imported from backend)
    this.referenceAnnotationIds = [];

    // Selection data stored when user selects text.
    // Persists until Save (creates annotation) or Cancel (clears data).
    this.currentSelectionRects = null;
    this.currentSelectionRect = null;
    this.currentSelectionPageIndex = null;

    // Chat citation: temporary underline annotation shown for 5 seconds
    this.chatCitationAnnotationId = null;
    this.chatCitationTimeout = null;

    // Pin dropped or shape drawn whose reference has not been saved yet:
    // { id, pageIndex, caption }. Saved by confirmReference, removed again
    // when the labeller is dismissed without saving.
    this.pendingAnnotation = null;

    // Debounce timers for pin and shape move events, per annotation ID
    this.pendingMoveTimers = {};

    // Last pointer position, stored directly on instance for synchronous
    // access in onEndSelection (avoids race condition with setState)
    this.lastPointerCoordinates = null;

    this.state = {
      pdfReady: false, // Backend PDF rendition ready — gates viewer init
      pdfTimedOut: false, // Rendition still pending after the poll timeout
      referenceLabel: null,
      referenceDocumentLabel: null,
      creatingNewAnnotation: false,
      externalAnnotationSelected: false,
      selectedReference: null,
      selectedReferenceAnnotationId: null,
      currentAnnotation: null,
      currentAnnotationXfdf: null,
      currentAnnotationText: null,
      currentPageIndex: null,
      editReferenceLabelOpen: false,
      editAnnotationLabelOpen: false,
    };
  }

  // ─── Viewer Lifecycle ───────────────────────────────────────────────

  /**
   * Called when PaperView PDF fires its onReady event.
   * Initialises plugin references, subscribes to annotation and
   * selection events, and schedules the initial annotation import.
   */
  handleViewerReady = (registry) => {
    var annotProvider = registry.getPlugin("annotation");
    if (annotProvider) this.annotationPlugin = annotProvider.provides();

    var selProvider = registry.getPlugin("selection");
    if (selProvider) this.selectionPlugin = selProvider.provides();

    var xfdfProvider = registry.getPlugin("xfdf");
    if (xfdfProvider) this.xfdfPlugin = xfdfProvider.provides();

    var scrollProvider = registry.getPlugin("scroll");
    if (scrollProvider) this.scrollPlugin = scrollProvider.provides();

    var searchProvider = registry.getPlugin("search");
    if (searchProvider) this.searchPlugin = searchProvider.provides();

    var commandsProvider = registry.getPlugin("commands");
    if (commandsProvider) this.commandsPlugin = commandsProvider.provides();

    var uiProvider = registry.getPlugin("ui");
    if (uiProvider) this.uiPlugin = uiProvider.provides();

    var documentManagerProvider = registry.getPlugin("document-manager");
    if (documentManagerProvider) {
      this.documentManagerPlugin = documentManagerProvider.provides();
    }

    // Remember the user's zoom level for the next document they open.
    var zoomProvider = registry.getPlugin("zoom");
    if (zoomProvider) {
      this.zoomUnsubscribe = rememberZoomLevel(zoomProvider.provides());
    }

    // Expose functions to parent via refs
    if (this.props.chatCitationRef) {
      this.props.chatCitationRef.current = this.handleChatCitation;
    }
    if (this.props.deleteAnnotationRef) {
      this.props.deleteAnnotationRef.current = this.deleteAnnotationById;
    }
    if (this.props.selectAndScrollRef) {
      this.props.selectAndScrollRef.current = this.selectAnnotationAndScroll;
    }

    // Subscribe to events
    if (!this.props.approved && !this.props.readOnly) {
      this.subscribeToSelectionEvents();
    }
    this.subscribeToAnnotationClickEvents();
    this.removeSelectionMenus();
    this.setUpDrawnReferences();

    // Import annotations once the page layout is ready. A fixed delay loses
    // the race on slow loads and the import is then silently dropped.
    // onLayoutReady is a behavior emitter: it replays immediately when the
    // layout is already done.
    var _that = this;
    if (this.scrollPlugin && this.scrollPlugin.onLayoutReady) {
      var layoutAlreadyReady = false;
      var unsubscribeLayoutReady = this.scrollPlugin.onLayoutReady(function () {
        layoutAlreadyReady = true;
        if (unsubscribeLayoutReady) {
          unsubscribeLayoutReady();
          unsubscribeLayoutReady = null;
        }
        _that.loadAnnotations();
      });

      // The replay case runs the callback before the unsubscribe handle
      // exists — clean up here instead.
      if (layoutAlreadyReady && unsubscribeLayoutReady) {
        unsubscribeLayoutReady();
        unsubscribeLayoutReady = null;
      }
    } else {
      setTimeout(function () {
        _that.loadAnnotations();
      }, 500);
    }
  };

  /**
   * Removes Paperview PDF's popup menus for selected text and selected
   * annotations (delete, annotation style, comment). The reference viewer
   * has its own labeller for both, and references are deleted from the
   * sidebar.
   *
   * mergeSchema replaces the whole selectionMenus map, so listing only an
   * empty text selection menu also drops the annotation menu.
   */
  removeSelectionMenus = () => {
    if (!this.uiPlugin) return;

    this.uiPlugin.mergeSchema({
      selectionMenus: {
        selection: {
          id: "selection",
          items: [],
        },
      },
    });
  };

  // ─── XFDF Import ───────────────────────────────────────────────────

  /**
   * Imports an XFDF string into the viewer, skipping annotations
   * that have already been imported (deduplication).
   */
  importXfdfString = (xfdfString) => {
    if (!this.xfdfPlugin || !xfdfString) return;
    try {
      this.xfdfPlugin.importXfdf(xfdfString);
    } catch (err) {
      // Import failed — viewer still usable
    }
  };

  /**
   * Imports the annotation of a reference.
   *
   * Pin drops (stamps) cannot go through the xfdf plugin: it does not carry
   * the stamp's image, so the pin would be imported without an appearance
   * and stay invisible. Pins are imported with their image by importPinXfdf;
   * everything else goes through importXfdfString.
   *
   * @param {string} xfdfString - the reference's xfdf_highlight_string
   * @param {string} annotationId - the reference's xfdf_highlight_id
   */
  importReferenceXfdf = (xfdfString, annotationId) => {
    var _that = this;

    importPinXfdf({
      annotationPlugin: this.annotationPlugin,
      documentManagerPlugin: this.documentManagerPlugin,
      xfdfString: xfdfString,
      annotationId: annotationId,
      type: PIN_TYPES.REFERENCE,
    })
      .catch(function () {
        return false;
      })
      .then(function (isPinImported) {
        if (!isPinImported) {
          _that.importXfdfString(xfdfString);
        }
      });
  };

  /**
   * Checks whether an annotation with the given ID already exists
   * in the viewer's annotation state.
   */
  annotationAlreadyImported = (annotationId) => {
    if (!this.annotationPlugin || !annotationId) return false;
    var state = this.annotationPlugin.getState();
    if (!state || !state.byUid) return false;
    return !!state.byUid[annotationId];
  };

  /**
   * Imports reference annotations from the backend data (props.references)
   * into the viewer. After import, classifies all annotations as either
   * reference or native, then hides native ones by default.
   */
  loadAnnotations = () => {
    if (!this.props.references) return;

    // Track the reference document label from the first reference
    if (this.props.references[0]) {
      this.setState({
        referenceDocumentLabel:
          this.props.references[0].document.reference_document_label,
      });
    }

    // Import each reference's XFDF
    var _that = this;
    this.props.references.forEach(function (reference) {
      if (
        reference.xfdf_highlight_id &&
        reference.xfdf_highlight_string &&
        !_that.annotationAlreadyImported(reference.xfdf_highlight_id)
      ) {
        _that.importReferenceXfdf(
          reference.xfdf_highlight_string,
          reference.xfdf_highlight_id
        );
        _that.referenceAnnotationIds.push(reference.xfdf_highlight_id);
      }
    });

    // After import, classify annotations and hide native ones
    setTimeout(function () {
      _that.classifyAndHideNativeAnnotations();

      // If a specific highlight should be selected on load, select it
      if (_that.props.xfdfHightlightId) {
        _that.selectAnnotationAndScroll(
          _that.props.xfdfHightlightId,
          _that.getPageNumberForReference(_that.props.xfdfHightlightId)
        );
      }
    }, 500);
  };

  /**
   * Looks up the page number for a reference annotation by its ID.
   */
  getPageNumberForReference = (annotationId) => {
    if (!this.props.references) return null;
    for (var i = 0; i < this.props.references.length; i++) {
      if (this.props.references[i].xfdf_highlight_id === annotationId) {
        return this.props.references[i].page.page_number;
      }
    }
    return null;
  };

  /**
   * After all annotations are loaded, identifies native (non-reference)
   * annotations and hides them if the toggle is set to hidden (default).
   */
  classifyAndHideNativeAnnotations = () => {
    if (!this.annotationPlugin) return;
    var state = this.annotationPlugin.getState();
    if (!state || !state.byUid) return;

    // Build snapshot of native annotations
    var snapshot = [];
    var uids = Object.keys(state.byUid);
    for (var i = 0; i < uids.length; i++) {
      var id = uids[i];
      var tracked = state.byUid[id];
      if (tracked.commitState === "deleted") continue;
      if (!this.isReferenceAnnotation(id)) {
        snapshot.push({
          id: id,
          pageIndex: tracked.object.pageIndex,
          object: Object.assign({}, tracked.object),
        });
      }
    }
    this.nativeAnnotationSnapshot = snapshot;

    // Hide native annotations by default
    if (this.props.hideOutsideAnnotations) {
      this.hideExternalAnnots();
    }
  };

  // ─── Selection Events ──────────────────────────────────────────────

  /**
   * Subscribes to text selection events in the PaperView PDF viewer.
   * When the user finishes selecting text, stores the selection data,
   * shows the reference labeller modal immediately, and fetches the
   * selected text asynchronously in the background.
   *
   * The labeller modal is rendered synchronously in onEndSelection
   * rather than being gated behind the async getSelectedText() call,
   * because PdfTask.wait() may not always fire its callback reliably.
   */
  subscribeToSelectionEvents = () => {
    if (!this.selectionPlugin) return;

    var _that = this;

    // Track pointer position on every pointerup so it is available
    // when onEndSelection fires (they may run in the same tick).
    var viewerContainer = document.getElementById("webViewerContainer");
    if (viewerContainer) {
      viewerContainer.addEventListener(
        "pointerup",
        function (e) {
          _that.lastPointerCoordinates = { x: e.pageX, y: e.pageY };
        },
        true
      ); // capture phase — fires before onEndSelection
    }

    this.selectionPlugin.onEndSelection(function (selectionData) {
      if (!selectionData) return;

      // Store selection data for annotation creation on Save
      var formattedSelection = _that.selectionPlugin.getFormattedSelection();
      if (!formattedSelection || formattedSelection.length === 0) return;

      // Selecting text starts a different reference: drop an unsaved pin
      // or shape.
      _that.discardPendingAnnotation();

      var firstPage = formattedSelection[0];
      _that.currentSelectionRects = firstPage.segmentRects;
      _that.currentSelectionRect = firstPage.rect;
      _that.currentSelectionPageIndex = firstPage.pageIndex;

      var pageNumber = firstPage.pageIndex + 1;

      // Auto-generate reference label
      var pageRefCount = 0;
      var referenceDocumentLabel = _that.state.referenceDocumentLabel
        ? _that.state.referenceDocumentLabel
        : _that.props.referenceDocumentLabel;

      _that.props.references.forEach(function (ref) {
        if (ref.page.page_number === pageNumber) {
          pageRefCount += 1;
        }
      });

      var letterSequence =
        pageRefCount < 26
          ? numberToEncodedLetter(pageRefCount + 1)
          : numberToEncodedLetter(pageRefCount);
      var referenceLabel = "pg" + pageNumber + " " + letterSequence;

      // Fetch selected text before clearing — try both API styles
      var textTask = _that.selectionPlugin.getSelectedText();
      var textCallback = function (textArray) {
        var selectedText = textArray ? textArray.join("\n") : "";
        _that.setState({ currentAnnotationText: selectedText });
      };

      if (textTask && typeof textTask.wait === "function") {
        textTask.wait(textCallback, function () {});
      } else if (textTask && typeof textTask.then === "function") {
        textTask.then(textCallback);
      }

      // Update state and show labeller
      _that.setState({
        currentPageIndex: pageNumber,
        currentAnnotationText: "",
        referenceLabel: referenceLabel,
        referenceDocumentLabel: referenceDocumentLabel,
        creatingNewAnnotation: true,
        externalAnnotationSelected: false,
      });

      _that.renderLabellerModal(referenceDocumentLabel, referenceLabel);
    });
  };

  // ─── Annotation Click Events ───────────────────────────────────────

  /**
   * Subscribes to annotation selection changes. When a user clicks an
   * annotation, determines if it is a reference or native annotation
   * and opens the labeller modal accordingly.
   */
  subscribeToAnnotationClickEvents = () => {
    if (!this.annotationPlugin) return;

    var _that = this;
    var previousSelectedUid = null;

    this.annotationPlugin.onStateChange(function (stateEvent) {
      // The event wraps the document's annotation state: { documentId, state }
      var state = stateEvent.state || stateEvent;
      var selectedUid = state.selectedUid;

      // Only react to newly selected annotations
      if (selectedUid === previousSelectedUid) return;
      previousSelectedUid = selectedUid;

      if (!selectedUid) {
        // Annotation deselected
        _that.props.onAnnotationDeselect();
        return;
      }

      var tracked = state.byUid[selectedUid];
      if (!tracked || tracked.commitState === "deleted") return;

      var annotObj = tracked.object;
      var annotationType = annotObj.type;

      // Only handle highlight, underline, strikeout, squiggly and pins.
      // annotObj.type is a PdfAnnotationSubtype enum value (a number).
      var textMarkupTypes = [
        PdfAnnotationSubtype.HIGHLIGHT,
        PdfAnnotationSubtype.UNDERLINE,
        PdfAnnotationSubtype.STRIKEOUT,
        PdfAnnotationSubtype.SQUIGGLY,
      ];
      var isTextMarkup = false;
      for (var i = 0; i < textMarkupTypes.length; i++) {
        if (annotationType === textMarkupTypes[i]) {
          isTextMarkup = true;
          break;
        }
      }
      var isDrawn = isPinAnnotation(annotObj) || isShapeAnnotation(annotObj);
      if (!isTextMarkup && !isDrawn) return;

      var pageIndex = annotObj.pageIndex;
      var annotationId = annotObj.id;
      var selectedReference = _that.findReferenceByAnnotationId(annotationId);

      // A pin or shape only opens the labeller once it is a saved reference.
      // One that was just dropped or drawn already has the labeller open,
      // and any other stamp or shape in the PDF is not a source for a new
      // reference.
      if (isDrawn && !selectedReference) return;

      // Clicking another annotation starts a different reference: drop an
      // unsaved pin or shape.
      _that.discardPendingAnnotation();

      _that.setState({
        selectedReference: selectedReference,
        selectedReferenceAnnotationId: annotationId,
        currentAnnotation: annotObj,
      });

      if (selectedReference && !_that.props.approved && !_that.props.readOnly) {
        // Existing Papercurve reference — show labeller with existing data,
        // read-only until the user clicks a pencil icon to edit a label
        _that.renderExistingReferenceLabeller(selectedReference);
      } else if (
        !_that.props.approved &&
        !_that.props.readOnly &&
        !selectedReference
      ) {
        // Native annotation — treat as potential new reference source
        // Try to get text from annotation data
        var annotationText = "";
        if (annotObj.custom && annotObj.custom.text) {
          annotationText = annotObj.custom.text;
        } else if (annotObj.contents) {
          annotationText = annotObj.contents;
        }

        var pageNumber = pageIndex + 1;
        var pageRefCount = 0;
        var referenceDocumentLabel = _that.state.referenceDocumentLabel
          ? _that.state.referenceDocumentLabel
          : _that.props.referenceDocumentLabel;

        _that.props.references.forEach(function (ref) {
          if (ref.page.page_number === pageNumber) {
            pageRefCount += 1;
          }
        });

        var letterSequence =
          pageRefCount < 26
            ? numberToEncodedLetter(pageRefCount + 1)
            : numberToEncodedLetter(pageRefCount);
        var referenceLabel = "pg" + pageNumber + " " + letterSequence;

        _that.setState({
          referenceLabel: referenceLabel,
          referenceDocumentLabel: referenceDocumentLabel,
          creatingNewAnnotation: true,
          currentPageIndex: pageNumber,
          externalAnnotationSelected: true,
          currentAnnotationText: annotationText,
        });

        _that.renderLabellerModal(referenceDocumentLabel, referenceLabel);
      }

      // Notify parent of annotation selection
      _that.props.onAnnotationSelect(pageIndex, annotationId);
    });
  };

  // ─── Pin Drop and Shape References ────────────────────────────────

  /**
   * Adds the pin button to the toolbar and listens for dropped pins and
   * drawn shapes (the Shapes toolbar is Paperview PDF's own). Both behave
   * like a text selection: they open the labeller, and the reference is
   * saved when the user clicks Save.
   * Nothing can be dropped or drawn on approved or read-only documents.
   */
  setUpDrawnReferences = () => {
    var _that = this;
    var canDraw = !this.props.approved && !this.props.readOnly;

    registerPinCommands({
      commandsPlugin: this.commandsPlugin,
      uiPlugin: this.uiPlugin,
      pinTypes: REFERENCE_PIN_TYPES,
      onTogglePinTool: function (pinType) {
        togglePinTool(_that.annotationPlugin, pinType);
      },
    });
    updatePinToolbar({
      uiPlugin: this.uiPlugin,
      pinTypes: REFERENCE_PIN_TYPES,
      isEnabled: canDraw,
    });

    // The viewer config disables the category in both the ui plugin (hides
    // the tab) and the commands plugin (disables the tab's command), so both
    // have to be switched.
    var categoryPlugins = [this.uiPlugin, this.commandsPlugin];
    categoryPlugins.forEach(function (plugin) {
      if (!plugin) return;
      if (canDraw) {
        plugin.enableCategory(SHAPES_MODE_CATEGORY);
      } else {
        plugin.disableCategory(SHAPES_MODE_CATEGORY);
      }
    });

    if (!canDraw || !this.annotationPlugin) return;

    this.annotationPlugin.onAnnotationEvent(function (event) {
      var annotation = event.annotation;
      if (!annotation) return;

      // committed=true events are the engine's echo of an action that was
      // already handled.
      if (event.committed) return;

      var isPin = isPinAnnotation(annotation);
      var isShape = isShapeAnnotation(annotation);
      if (!isPin && !isShape) return;

      if (event.type === "create") {
        if (isShape) {
          // The pin tool switches itself off after one pin; a shape tool
          // stays on, and would swallow the clicks meant for the labeller.
          _that.annotationPlugin.setActiveTool(null);
        }

        _that.handleAnnotationDrawn(
          event,
          isPin ? PIN_REFERENCE_CAPTION : SHAPE_REFERENCE_CAPTION
        );
        return;
      }

      if (event.type === "update") {
        _that.debouncedReferenceMove(annotation.id);
      }
    });
  };

  /**
   * Saves a saved reference pin's or shape's new geometry after the user
   * moved or resized it. Debounced per annotation: Paperview PDF fires
   * several update events during one drag, and only the final one is saved.
   *
   * An annotation whose reference has not been saved yet is skipped: it is
   * exported from its current state when the user clicks Save
   * (see confirmDrawnReference).
   *
   * @param {string} annotationId - id of the annotation that was moved
   */
  debouncedReferenceMove = (annotationId) => {
    var _that = this;

    if (this.pendingMoveTimers[annotationId]) {
      clearTimeout(this.pendingMoveTimers[annotationId]);
    }

    this.pendingMoveTimers[annotationId] = setTimeout(function () {
      delete _that.pendingMoveTimers[annotationId];

      var reference = _that.findReferenceByAnnotationId(annotationId);
      if (!reference) return;
      if (!_that.annotationPlugin || !_that.xfdfPlugin) return;
      if (!_that.props.onUpdateReferenceXfdf) return;

      // Export from plugin state: the update event still carries the
      // annotation's previous geometry.
      var state = _that.annotationPlugin.getState();
      var tracked = state && state.byUid ? state.byUid[annotationId] : null;
      if (!tracked || !tracked.object) return;

      // The reference's XFDF is replaced as a whole, so it is exported as a
      // creation (<add>), the same form it had when first saved.
      var xfdfString = _that.xfdfPlugin.exportAnnotationEvent({
        type: "create",
        annotation: tracked.object,
        pageIndex: tracked.object.pageIndex,
        documentId: undefined,
        committed: false,
      });
      if (!xfdfString) return;

      _that.props.onUpdateReferenceXfdf(
        reference.id,
        xfdfString,
        reference.document.reference_document_label
      );
    }, 300);
  };

  /**
   * Builds the default annotation label for a new reference on a page:
   * "pg{page} {letter}", the letter following the references already on
   * that page.
   *
   * @param {number} pageNumber - one-based page number
   * @returns {string}
   */
  buildReferenceLabel = (pageNumber) => {
    var pageRefCount = 0;

    this.props.references.forEach(function (ref) {
      if (ref.page.page_number === pageNumber) {
        pageRefCount += 1;
      }
    });

    var letterSequence =
      pageRefCount < 26
        ? numberToEncodedLetter(pageRefCount + 1)
        : numberToEncodedLetter(pageRefCount);

    return "pg" + pageNumber + " " + letterSequence;
  };

  /**
   * Opens the labeller for a pin that was just dropped or a shape that was
   * just drawn, the same way a text selection does. Nothing is saved until
   * the user clicks Save (see confirmReference).
   *
   * @param {Object} event - the annotation plugin's create event
   * @param {string} caption - caption saved with the reference, since there
   *   is no selected text
   */
  handleAnnotationDrawn = (event, caption) => {
    // Only one unsaved pin or shape at a time: a new one replaces it.
    this.discardPendingAnnotation();

    // Not made from selected text: forget any pending selection.
    this.currentSelectionRects = null;
    this.currentSelectionRect = null;
    this.currentSelectionPageIndex = null;
    if (this.selectionPlugin) {
      this.selectionPlugin.clear();
    }

    this.pendingAnnotation = {
      id: event.annotation.id,
      pageIndex: event.pageIndex,
      caption: caption,
    };

    var pageNumber = event.pageIndex + 1;
    var referenceLabel = this.buildReferenceLabel(pageNumber);
    var referenceDocumentLabel = this.state.referenceDocumentLabel
      ? this.state.referenceDocumentLabel
      : this.props.referenceDocumentLabel;

    this.setState({
      currentPageIndex: pageNumber,
      currentAnnotationText: caption,
      referenceLabel: referenceLabel,
      referenceDocumentLabel: referenceDocumentLabel,
      creatingNewAnnotation: true,
      externalAnnotationSelected: false,
    });

    this.renderLabellerModal(referenceDocumentLabel, referenceLabel);
  };

  /**
   * Saves the reference for the pin or shape that is waiting in the
   * labeller. The XFDF is exported from the annotation's current state, so
   * one the user moved or resized before saving is saved as it is now.
   */
  confirmDrawnReference = () => {
    var pending = this.pendingAnnotation;
    if (!pending || !this.annotationPlugin || !this.xfdfPlugin) return;

    var state = this.annotationPlugin.getState();
    var tracked = state && state.byUid ? state.byUid[pending.id] : null;
    if (!tracked || !tracked.object) return;

    var annotObj = tracked.object;

    // Event type must be "create" (maps to <add> in XFDF)
    var xfdfString = this.xfdfPlugin.exportAnnotationEvent({
      type: "create",
      annotation: annotObj,
      pageIndex: annotObj.pageIndex,
      documentId: undefined,
      committed: false,
    });

    // The annotation is now a reference: keep it when the labeller closes.
    this.pendingAnnotation = null;
    this.referenceAnnotationIds.push(annotObj.id);

    this.props.onAddReference(
      annotObj.pageIndex + 1, // 1-based page number
      annotObj.id,
      xfdfString,
      pending.caption,
      this.state.referenceLabel,
      this.state.referenceDocumentLabel
    );
  };

  /**
   * Removes a dropped pin or drawn shape whose reference was never saved,
   * e.g. when the labeller is dismissed or the user starts a different
   * reference.
   */
  discardPendingAnnotation = () => {
    var pending = this.pendingAnnotation;
    if (!pending) return;

    this.pendingAnnotation = null;
    this.deleteAnnotationById(pending.id);
  };

  // ─── Annotation Creation ───────────────────────────────────────────

  /**
   * Creates a highlight annotation from the stored text selection data,
   * exports the XFDF, and calls onAddReference to save the reference.
   * Called when the user clicks Save in the labeller modal.
   */
  createAnnotationFromSelection = () => {
    if (
      !this.annotationPlugin ||
      !this.xfdfPlugin ||
      !this.currentSelectionRects
    ) {
      return;
    }

    var pageIndex = this.currentSelectionPageIndex;
    var annotId = uuidV4();

    var annotObj = {
      type: PdfAnnotationSubtype.HIGHLIGHT,
      id: annotId,
      created: new Date(),
      flags: ["print"],
      blendMode: PdfBlendMode.Multiply,
      pageIndex: pageIndex,
      rect: this.currentSelectionRect,
      segmentRects: this.currentSelectionRects,
      color: "#7a6a8d",
      opacity: 0.5,
    };

    // Create the annotation in the viewer (renders the highlight)
    this.annotationPlugin.createAnnotation(pageIndex, annotObj);

    // Clear the text selection overlay
    if (this.selectionPlugin) {
      this.selectionPlugin.clear();
    }

    // Export XFDF — event type must be "create" (maps to <add> in XFDF)
    var syntheticEvent = {
      type: "create",
      annotation: annotObj,
      pageIndex: pageIndex,
      documentId: undefined,
      committed: false,
    };
    var xfdfString = this.xfdfPlugin.exportAnnotationEvent(syntheticEvent);

    // Track this as a reference annotation
    this.referenceAnnotationIds.push(annotId);

    this.props.onAddReference(
      this.state.currentPageIndex, // 1-based page number
      annotId,
      xfdfString,
      this.state.currentAnnotationText,
      this.state.referenceLabel,
      this.state.referenceDocumentLabel
    );

    // Clear stored selection data
    this.currentSelectionRects = null;
    this.currentSelectionRect = null;
    this.currentSelectionPageIndex = null;
  };

  // ─── Reference Helpers ─────────────────────────────────────────────

  /**
   * Checks whether the given annotation ID belongs to a
   * reference annotation (imported from backend).
   */
  isReferenceAnnotation = (annotationId) => {
    for (var i = 0; i < this.referenceAnnotationIds.length; i++) {
      if (this.referenceAnnotationIds[i] === annotationId) return true;
    }
    // Also check against props.references
    if (this.props.references) {
      for (var j = 0; j < this.props.references.length; j++) {
        if (this.props.references[j].xfdf_highlight_id === annotationId) {
          return true;
        }
      }
    }
    return false;
  };

  /**
   * Finds the reference object from props.references that matches
   * the given annotation ID. Returns null if not found.
   */
  findReferenceByAnnotationId = (annotationId) => {
    if (!this.props.references) return null;
    for (var i = 0; i < this.props.references.length; i++) {
      if (this.props.references[i].xfdf_highlight_id === annotationId) {
        return this.props.references[i];
      }
    }
    return null;
  };

  // ─── Labeller Modal ────────────────────────────────────────────────

  /**
   * Shows the reference labeller modal, positioning it near the current
   * mouse position and populating the input fields.
   */
  renderLabellerModal = (referenceDocumentLabel, referenceLabel) => {
    var referenceLabellerContainer = document.getElementById(
      "referenceLabellerContainer"
    );
    if (!referenceLabellerContainer) return;

    // Position the labeller near the last pointer position.
    // Uses instance variable (set synchronously in capture-phase pointerup)
    // rather than React state to avoid race conditions with onEndSelection.
    var viewerContainer = document.getElementById("webViewerContainer");
    var containerRect = viewerContainer
      ? viewerContainer.getBoundingClientRect()
      : { top: 0, height: 400 };
    var coords = this.lastPointerCoordinates;
    if (coords) {
      var relativeY = coords.y - containerRect.top;
      referenceLabellerContainer.style.top = relativeY + "px";
    } else {
      // Fallback: center vertically in the viewer container
      referenceLabellerContainer.style.top =
        Math.round(containerRect.height / 3) + "px";
    }

    // Populate input fields
    var referenceDocumentLabelInput = document.getElementById(
      "referenceDocumentLabelInput"
    );
    if (referenceDocumentLabelInput) {
      referenceDocumentLabelInput.value = referenceDocumentLabel || "";
    }

    var referenceLabelInput = document.getElementById("referenceLabelInput");
    if (referenceLabelInput) {
      referenceLabelInput.value = referenceLabel || "";
    }

    referenceLabellerContainer.style.display = "table";

    // Configure input editability
    if (!referenceDocumentLabel) {
      // New reference document — both fields editable
      if (referenceDocumentLabelInput) {
        referenceDocumentLabelInput.style.background = "white";
        referenceDocumentLabelInput.disabled = false;
      }
      if (referenceLabelInput) {
        referenceLabelInput.style.background = "white";
        referenceLabelInput.disabled = false;
      }
      var refLabelPencilIcon = document.getElementById("refLabelPencilIcon");
      if (refLabelPencilIcon) refLabelPencilIcon.style.display = "none";
      var refDocLabelPencilIcon = document.getElementById(
        "refDocLabelPencilIcon"
      );
      if (refDocLabelPencilIcon) refDocLabelPencilIcon.style.display = "none";
    } else {
      // Existing reference document — annotation label editable
      if (referenceLabelInput) {
        referenceLabelInput.style.background = "white";
        referenceLabelInput.disabled = false;
      }
      var refLabelPencilIcon2 = document.getElementById("refLabelPencilIcon");
      if (refLabelPencilIcon2) refLabelPencilIcon2.style.display = "none";
    }
  };

  /**
   * Shows the labeller for a reference that already exists (a saved
   * highlight or pin the user clicked), so its labels can be edited.
   *
   * Both fields show the reference's current reference ID and annotation ID
   * and are read-only; each has a pencil icon that makes it editable
   * (handleEditRefDocLabel / handleEditRefLabel).
   *
   * @param {Object} selectedReference - the reference from props.references
   */
  renderExistingReferenceLabeller = (selectedReference) => {
    var referenceDocumentLabel =
      selectedReference.document.reference_document_label;
    var referenceLabel = selectedReference.label;

    this.setState({
      creatingNewAnnotation: false,
      externalAnnotationSelected: false,
      editReferenceLabelOpen: false,
      editAnnotationLabelOpen: false,
      referenceLabel: referenceLabel,
      referenceDocumentLabel: referenceDocumentLabel,
    });

    // Positions the labeller, fills in both fields and shows it
    this.renderLabellerModal(referenceDocumentLabel, referenceLabel);

    // renderLabellerModal leaves fields editable for the create flow; an
    // existing reference starts read-only with its pencil icons showing.
    var lockInput = function (inputId, pencilIconId, checkmarkId) {
      var input = document.getElementById(inputId);
      if (input) {
        input.disabled = true;
        input.style.background = "rgba(234, 235, 239, 1)";
        input.style.borderColor = "#D6DCE8";
      }

      var pencilIcon = document.getElementById(pencilIconId);
      if (pencilIcon) pencilIcon.style.display = "block";

      var checkmark = document.getElementById(checkmarkId);
      if (checkmark) checkmark.style.display = "none";
    };

    lockInput(
      "referenceDocumentLabelInput",
      "refDocLabelPencilIcon",
      "refDocLabelCheckmark"
    );
    lockInput("referenceLabelInput", "refLabelPencilIcon", "refLabelCheckmark");
  };

  /**
   * Hides the reference labeller modal and resets error states.
   */
  hideLabellerModal = () => {
    // A pin or shape whose reference was not saved must not stay on the
    // page. After a save there is nothing pending (see confirmDrawnReference).
    this.discardPendingAnnotation();

    // Clear any pending selection data and dismiss the selection overlay
    this.currentSelectionRects = null;
    this.currentSelectionRect = null;
    this.currentSelectionPageIndex = null;
    if (this.selectionPlugin) {
      this.selectionPlugin.clear();
    }

    this.setState({
      creatingNewAnnotation: false,
    });

    var refDocLabelExistsError = document.getElementById(
      "refDocLabelExistsError"
    );
    if (refDocLabelExistsError) refDocLabelExistsError.style.display = "none";

    var refLabelError = document.getElementById("refLabelError");
    if (refLabelError) refLabelError.style.display = "none";

    var refLabelEmptyError = document.getElementById("refLabelEmptyError");
    if (refLabelEmptyError) refLabelEmptyError.style.display = "none";

    var refDocLabelError = document.getElementById("refDocLabelError");
    if (refDocLabelError) refDocLabelError.style.display = "none";

    var referenceLabellerContainer = document.getElementById(
      "referenceLabellerContainer"
    );
    if (referenceLabellerContainer) {
      referenceLabellerContainer.style.display = "none";
    }

    this.closeChangeSubmission(false, true);
  };

  // ─── Form Handlers (labeller modal) ────────────────────────────────

  handleChange = (event) => {
    var target = event.target;
    var value = target.value;
    var name = target.name;

    this.setState({
      [name]: value,
    });

    var referenceLabelInput = document.getElementById("referenceLabelInput");
    var referenceDocumentLabelInput = document.getElementById(
      "referenceDocumentLabelInput"
    );

    if (
      target.name === "referenceDocumentLabel" &&
      target.value.trim() !== ""
    ) {
      var refDocLabelExistsError = document.getElementById(
        "refDocLabelExistsError"
      );
      if (refDocLabelExistsError) refDocLabelExistsError.style.display = "none";

      var refDocLabelError = document.getElementById("refDocLabelError");
      if (refDocLabelError) refDocLabelError.style.display = "none";

      if (referenceDocumentLabelInput) {
        referenceDocumentLabelInput.style.borderColor = "#D6DCE8";
      }
    }

    if (target.name === "referenceLabel" && target.value.trim() !== "") {
      var refLabelError = document.getElementById("refLabelError");
      var refLabelEmptyError = document.getElementById("refLabelEmptyError");
      if (refLabelError) refLabelError.style.display = "none";
      if (refLabelEmptyError) refLabelEmptyError.style.display = "none";

      if (referenceLabelInput) {
        referenceLabelInput.style.borderColor = "#D6DCE8";
      }
    }
  };

  validateInput = () => {
    var refDocLabelError = document.getElementById("refDocLabelError");
    var refDocLabelExistsError = document.getElementById(
      "refDocLabelExistsError"
    );
    var refLabelEmptyError = document.getElementById("refLabelEmptyError");
    var refLabelError = document.getElementById("refLabelError");
    var referenceLabelInput = document.getElementById("referenceLabelInput");
    var referenceDocumentLabelInput = document.getElementById(
      "referenceDocumentLabelInput"
    );

    var referenceDocumentLabel =
      this.props.references.length > 0
        ? this.props.references[0].document.reference_document_label
        : null;

    var refDocLabelExists = false;

    this.props.sharedReferences.shared_references.forEach(
      function (ref) {
        if (
          ref.reference_document_label &&
          ref.reference_document_label.trim().toLowerCase() ===
            referenceDocumentLabelInput.value.trim().toLowerCase()
        ) {
          if (this.props.referenceDocumentLabel) {
            if (
              referenceDocumentLabelInput.value.trim().toLowerCase() !==
              this.props.referenceDocumentLabel.trim().toLowerCase()
            ) {
              refDocLabelExists = true;
            }
          } else {
            refDocLabelExists = true;
          }
        }
      }.bind(this)
    );

    var refLabelExists = false;
    var selectedReference = null;

    if (this.props.currentRefLabel) {
      if (referenceLabelInput.value === this.props.currentRefLabel) {
        refLabelError.style.display = "block";
        referenceLabelInput.style.borderColor = "#e98b86";
        return false;
      }
    }

    var _that = this;
    this.props.references.forEach(function (ref) {
      if (
        ref.label &&
        ref.label.trim().toLowerCase() ===
          referenceLabelInput.value.trim().toLowerCase()
      ) {
        refLabelExists = true;
      }
      if (_that.state.currentAnnotation) {
        if (ref.xfdf_highlight_id === _that.state.currentAnnotation.id) {
          selectedReference = ref;
        }
      }
    });

    var refDocLabelEmpty = referenceDocumentLabelInput.value.trim() === "";
    var refLabelEmpty = referenceLabelInput.value.trim() === "";

    if (selectedReference) {
      if (
        refLabelExists &&
        selectedReference.label !== referenceLabelInput.value.trim()
      ) {
        refLabelError.style.display = "block";
        referenceLabelInput.style.borderColor = "#e98b86";
        return false;
      } else if (refDocLabelEmpty) {
        refDocLabelError.style.display = "block";
        referenceDocumentLabelInput.style.borderColor = "#e98b86";
        return false;
      } else if (refLabelEmpty) {
        refLabelEmptyError.style.display = "block";
        referenceLabelInput.style.borderColor = "#e98b86";
        return false;
      } else if (
        refDocLabelExists &&
        selectedReference.document.reference_document_label !==
          referenceDocumentLabelInput.value.trim()
      ) {
        refDocLabelExistsError.style.display = "block";
        referenceDocumentLabelInput.style.borderColor = "#e98b86";
        return false;
      } else {
        return true;
      }
    } else {
      if (refLabelExists) {
        refLabelError.style.display = "block";
        referenceLabelInput.style.borderColor = "#e98b86";
        return false;
      } else if (refDocLabelEmpty) {
        refDocLabelError.style.display = "block";
        referenceDocumentLabelInput.style.borderColor = "#e98b86";
        return false;
      } else if (refLabelEmpty) {
        refLabelEmptyError.style.display = "block";
        referenceLabelInput.style.borderColor = "#e98b86";
        return false;
      } else if (
        refDocLabelExists &&
        referenceDocumentLabel !== referenceDocumentLabelInput.value.trim()
      ) {
        refDocLabelExistsError.style.display = "block";
        referenceDocumentLabelInput.style.borderColor = "#e98b86";
        return false;
      } else {
        return true;
      }
    }
  };

  handleSave = (e) => {
    e.preventDefault();

    if (this.validateInput()) {
      // Editing an existing reference's "annotation id" field. The change is
      // persisted through handleChangeSubmission() (the same path used by the
      // inline checkmark), not by creating a new annotation via
      // confirmReference() — there is no text selection to build one from.
      // Guarded with !creatingNewAnnotation so the create flow (where the user
      // can also open the annotation-id field via the pencil) still falls
      // through to confirmReference() and actually creates the annotation.
      if (
        this.state.editAnnotationLabelOpen &&
        !this.state.creatingNewAnnotation
      ) {
        this.handleChangeSubmission();
        this.hideLabellerModal();
        return;
      }

      // Confirm the reference FIRST — needs currentSelectionRects
      // which hideLabellerModal would clear.
      if (this.props.linkingReference && !this.state.creatingNewAnnotation) {
        this.confirmExistingReference();
      } else {
        this.confirmReference();
      }

      // THEN hide the labeller (clears selection data)
      if (this.state.editReferenceLabelOpen) {
        this.handleConfirmRefDocLabel();
      } else {
        this.hideLabellerModal();
      }
    }
  };

  /**
   * Confirms a reference from an existing (already selected) annotation.
   * Used when linking an existing reference annotation to a parent document.
   */
  confirmExistingReference = () => {
    var referenceDocumentLabel = this.state.referenceDocumentLabel;
    var annotationId = this.state.selectedReferenceAnnotationId;

    if (!this.annotationPlugin || !annotationId) return;

    var state = this.annotationPlugin.getState();
    var tracked = state && state.byUid ? state.byUid[annotationId] : null;
    if (!tracked) return;

    var annotObj = tracked.object;

    this.props.onAddReference(
      annotObj.pageIndex, // 0-based (matches original PDFTron PageNumber - 1)
      annotationId,
      null,
      null,
      this.state.referenceLabel,
      referenceDocumentLabel
    );
  };

  /**
   * Confirms a new reference. If an external (native) annotation was
   * selected, exports its XFDF. If a pin was dropped or a shape drawn,
   * saves it.
   * Otherwise creates a new highlight annotation from the text selection.
   */
  confirmReference = () => {
    if (this.state.externalAnnotationSelected) {
      // External (native) annotation selected — export its XFDF
      var annotationId = this.state.selectedReferenceAnnotationId;
      if (!this.annotationPlugin || !this.xfdfPlugin || !annotationId) return;

      var state = this.annotationPlugin.getState();
      var tracked = state && state.byUid ? state.byUid[annotationId] : null;
      if (!tracked) return;

      var annotObj = tracked.object;
      var syntheticEvent = {
        type: "create",
        annotation: annotObj,
        pageIndex: annotObj.pageIndex,
        documentId: undefined,
        committed: false,
      };
      var xfdfString = this.xfdfPlugin.exportAnnotationEvent(syntheticEvent);

      // Track as reference
      this.referenceAnnotationIds.push(annotationId);

      this.props.onAddReference(
        annotObj.pageIndex + 1, // 1-based page number
        annotationId,
        xfdfString,
        this.state.currentAnnotationText,
        this.state.referenceLabel,
        this.state.referenceDocumentLabel
      );
    } else if (this.pendingAnnotation) {
      // Pin drop or shape — it already exists; export it and save the reference
      this.confirmDrawnReference();
    } else {
      // Text selection — create highlight annotation and export XFDF
      this.createAnnotationFromSelection();
    }
  };

  // ─── Label Edit Handlers ───────────────────────────────────────────

  handleChangeSubmission = () => {
    var referenceDocumentLabelInput = document.getElementById(
      "referenceDocumentLabelInput"
    );
    var referenceDocumentLabel = referenceDocumentLabelInput.value;

    var referenceLabelInput = document.getElementById("referenceLabelInput");
    var referenceLabel = referenceLabelInput.value;

    var selectedRefId = "";
    var _that = this;

    this.props.references.forEach(function (ref) {
      if (ref.xfdf_highlight_id === _that.state.selectedReferenceAnnotationId) {
        selectedRefId = ref.id;
      }
    });

    if (this.state.creatingNewAnnotation) {
      selectedRefId = this.props.references[0].id;
      referenceLabel = this.props.references[0].label;
      this.props.onUpdateReferenceLabel(
        selectedRefId,
        referenceLabel,
        referenceDocumentLabel
      );
    } else {
      this.props.onUpdateReferenceLabel(
        selectedRefId,
        referenceLabel,
        referenceDocumentLabel
      );
    }

    this.setState({ editAnnotationLabelOpen: false });
    this.closeChangeSubmission(true);
  };

  closeChangeSubmission = (valuesUpdated, enableRefDocLabelInput) => {
    if (valuesUpdated === undefined) valuesUpdated = false;
    if (enableRefDocLabelInput === undefined) enableRefDocLabelInput = false;

    var referenceDocumentLabel = this.state.referenceDocumentLabel;

    if (!valuesUpdated) {
      referenceDocumentLabel =
        this.props.references[0] !== undefined
          ? this.props.references[0].document.reference_document_label
          : null;
    }

    var refDocLabelPencilIcon = document.getElementById(
      "refDocLabelPencilIcon"
    );
    if (refDocLabelPencilIcon) refDocLabelPencilIcon.style.display = "block";

    var refLabelCheckmark = document.getElementById("refLabelCheckmark");
    if (refLabelCheckmark) refLabelCheckmark.style.display = "none";

    var refDocLabelCheckmark = document.getElementById("refDocLabelCheckmark");
    if (refDocLabelCheckmark) refDocLabelCheckmark.style.display = "none";

    var referenceDocumentLabelInput = document.getElementById(
      "referenceDocumentLabelInput"
    );
    if (referenceDocumentLabelInput) {
      referenceDocumentLabelInput.value = referenceDocumentLabel || "";
      referenceDocumentLabelInput.disabled = true;
      referenceDocumentLabelInput.style.background = "rgba(234, 235, 239, 1)";
      referenceDocumentLabelInput.style.borderColor = "#D6DCE8";
    }

    if (!this.state.creatingNewAnnotation || enableRefDocLabelInput) {
      var refLabelPencilIcon = document.getElementById("refLabelPencilIcon");
      if (refLabelPencilIcon) refLabelPencilIcon.style.display = "block";

      var referenceLabelInput = document.getElementById("referenceLabelInput");
      if (referenceLabelInput) {
        referenceLabelInput.disabled = true;
        referenceLabelInput.style.background = "rgba(234, 235, 239, 1)";
        referenceLabelInput.style.borderColor = "#D6DCE8";
      }
    }

    var referenceLabellerForm = document.getElementById(
      "referenceLabellerForm"
    );
    if (referenceLabellerForm) referenceLabellerForm.style.display = "flex";

    var refDocLabelConfirmationForm = document.getElementById(
      "refDocLabelConfirmationForm"
    );
    if (refDocLabelConfirmationForm) {
      refDocLabelConfirmationForm.style.display = "none";
    }

    this.setState({
      referenceDocumentLabel: referenceDocumentLabel,
    });
  };

  handleEditRefLabel = () => {
    var pencilIcon = document.getElementById("refLabelPencilIcon");
    if (pencilIcon) pencilIcon.style.display = "none";

    var checkmark = document.getElementById("refLabelCheckmark");
    if (checkmark) checkmark.style.display = "block";

    var referenceLabelInput = document.getElementById("referenceLabelInput");
    if (referenceLabelInput) {
      referenceLabelInput.disabled = false;
      referenceLabelInput.style.background = "white";
      referenceLabelInput.style.borderColor = "#1b8c96";
    }

    this.setState({ editAnnotationLabelOpen: true });
  };

  handleEditRefDocLabel = () => {
    var pencilIcon = document.getElementById("refDocLabelPencilIcon");
    if (pencilIcon) pencilIcon.style.display = "none";

    var checkmark = document.getElementById("refDocLabelCheckmark");
    if (checkmark) checkmark.style.display = "block";

    var referenceDocumentLabelInput = document.getElementById(
      "referenceDocumentLabelInput"
    );
    if (referenceDocumentLabelInput) {
      referenceDocumentLabelInput.disabled = false;
      referenceDocumentLabelInput.style.background = "white";
      referenceDocumentLabelInput.style.borderColor = "#1b8c96";
    }

    this.setState({ editReferenceLabelOpen: true });
  };

  handleConfirmRefDocLabel = () => {
    if (this.validateInput()) {
      var referenceLabellerForm = document.getElementById(
        "referenceLabellerForm"
      );
      if (referenceLabellerForm) referenceLabellerForm.style.display = "none";
      this.setState({ editReferenceLabelOpen: false });

      var confirmationForm = document.getElementById(
        "refDocLabelConfirmationForm"
      );
      if (confirmationForm) confirmationForm.style.display = "flex";
    }
  };

  handleConfirmRefLabel = () => {
    if (this.validateInput()) {
      this.handleChangeSubmission();
    }
  };

  // ─── Annotation Deletion & Navigation (exposed to parent) ─────────

  /**
   * Deletes a list of annotation IDs from the PaperView PDF viewer.
   * Marks IDs in toggleHiddenAnnotationIds so event handlers skip
   * API calls for these deletions.
   */
  deleteAnnotationsByIds = (annotationIds) => {
    if (!this.annotationPlugin) return;
    var state = this.annotationPlugin.getState();
    if (!state || !state.byUid) return;

    for (var i = 0; i < annotationIds.length; i++) {
      var id = annotationIds[i];
      var tracked = state.byUid[id];
      if (tracked && tracked.commitState !== "deleted") {
        this.toggleHiddenAnnotationIds[id] = true;
        this.annotationPlugin.deleteAnnotation(tracked.object.pageIndex, id);
      }
    }
  };

  /**
   * Deletes a single annotation by ID. Exposed to parent via
   * deleteAnnotationRef for use when deleting references from the sidebar.
   */
  deleteAnnotationById = (annotationId) => {
    if (!this.annotationPlugin || !annotationId) return;
    var state = this.annotationPlugin.getState();
    if (!state || !state.byUid) return;

    var tracked = state.byUid[annotationId];
    if (tracked && tracked.object) {
      this.toggleHiddenAnnotationIds[annotationId] = true;
      this.annotationPlugin.deleteAnnotation(
        tracked.object.pageIndex,
        annotationId
      );
    }
  };

  /**
   * Selects an annotation and scrolls to its page. Exposed to parent
   * via selectAndScrollRef for use when clicking references in the sidebar.
   */
  selectAnnotationAndScroll = (annotationId, pageNumber) => {
    if (!this.annotationPlugin || !this.scrollPlugin) return;

    // Select the annotation
    var state = this.annotationPlugin.getState();
    if (state && state.byUid && state.byUid[annotationId]) {
      var tracked = state.byUid[annotationId];
      this.annotationPlugin.selectAnnotation(
        tracked.object.pageIndex,
        annotationId
      );
    }

    // Scroll to the page
    if (pageNumber) {
      this.scrollPlugin.scrollToPage({
        pageNumber: pageNumber,
        behavior: "instant",
      });
    }
  };

  /**
   * Deselects all annotations in the viewer.
   */
  deselectReference = () => {
    if (!this.annotationPlugin) return;
    var state = this.annotationPlugin.getState();
    if (state && state.selectedUid) {
      this.annotationPlugin.deselectAnnotation();
    }
  };

  // ─── Visibility Toggles ────────────────────────────────────────────

  /**
   * Hides native (non-reference) annotations by deleting them
   * from the viewer. Stores a snapshot for re-import.
   */
  hideExternalAnnots = () => {
    if (!this.annotationPlugin) return;
    var state = this.annotationPlugin.getState();
    if (!state || !state.byUid) return;

    // Build or refresh the native annotation snapshot
    var snapshot = [];
    var uids = Object.keys(state.byUid);
    for (var i = 0; i < uids.length; i++) {
      var id = uids[i];
      var tracked = state.byUid[id];
      if (tracked.commitState === "deleted") continue;
      if (!this.isReferenceAnnotation(id)) {
        snapshot.push({
          id: id,
          pageIndex: tracked.object.pageIndex,
          object: Object.assign({}, tracked.object),
        });
      }
    }
    this.nativeAnnotationSnapshot = snapshot;

    var nativeIds = snapshot.map(function (s) {
      return s.id;
    });
    this.deleteAnnotationsByIds(nativeIds);
  };

  /**
   * Shows native annotations by re-creating them from the snapshot.
   */
  showExternalAnnots = () => {
    if (!this.annotationPlugin) return;

    for (var i = 0; i < this.nativeAnnotationSnapshot.length; i++) {
      var entry = this.nativeAnnotationSnapshot[i];
      this.annotationPlugin.createAnnotation(entry.pageIndex, entry.object);
    }
    this.nativeAnnotationSnapshot = [];
  };

  /**
   * Hides reference annotations by deleting them from the viewer.
   */
  hideReferenceAnnots = () => {
    this.deleteAnnotationsByIds(this.referenceAnnotationIds.slice());
  };

  /**
   * Shows reference annotations by re-importing their XFDF from
   * the backend data.
   */
  showReferenceAnnots = () => {
    if (!this.props.references) return;

    var _that = this;
    this.props.references.forEach(function (reference) {
      if (reference.xfdf_highlight_id && reference.xfdf_highlight_string) {
        _that.importReferenceXfdf(
          reference.xfdf_highlight_string,
          reference.xfdf_highlight_id
        );
      }
    });
  };

  // ─── Chat Citation ────────────────────────────────────────────────

  /**
   * Handles a chat citation click from the Paige AI chat panel.
   * Scrolls to the cited page and, if citation text is provided,
   * searches for it and creates a temporary red underline annotation
   * that auto-deletes after 5 seconds.
   *
   * Exposed to the parent (ReferenceViewer) via chatCitationRef.
   *
   * @param {Object} citationData - Citation data from the chat panel.
   * @param {Object} citationData.page - Page object with page_number.
   * @param {string} [citationData.text] - The cited text to highlight.
   */
  handleChatCitation = (citationData) => {
    if (!citationData || !citationData.page) return;

    var pageNumber = citationData.page.page_number;
    var text = citationData.text;

    // Scroll to the cited page
    if (this.scrollPlugin) {
      this.scrollPlugin.scrollToPage({
        pageNumber: pageNumber,
        behavior: "instant",
      });
    }

    if (!text) return;

    // Clear any existing citation highlight
    this.clearChatCitation();

    // Search requires the search plugin
    if (!this.searchPlugin || !this.annotationPlugin) return;

    var _that = this;
    var targetPageIndex = pageNumber - 1;

    var task = this.searchPlugin.searchAllPages(text);
    task.wait(
      function (searchResult) {
        if (!searchResult || !searchResult.results) return;

        // Find the first result on the target page
        var matchingResult = null;
        for (var i = 0; i < searchResult.results.length; i++) {
          if (searchResult.results[i].pageIndex === targetPageIndex) {
            matchingResult = searchResult.results[i];
            break;
          }
        }

        if (!matchingResult || !matchingResult.rects) return;

        // Build a bounding rect from the search result rects
        var boundingRect = null;
        for (var j = 0; j < matchingResult.rects.length; j++) {
          var r = matchingResult.rects[j];
          if (!boundingRect) {
            boundingRect = {
              origin: { x: r.origin.x, y: r.origin.y },
              size: { width: r.size.width, height: r.size.height },
            };
          } else {
            var minX = Math.min(boundingRect.origin.x, r.origin.x);
            var minY = Math.min(boundingRect.origin.y, r.origin.y);
            var maxX = Math.max(
              boundingRect.origin.x + boundingRect.size.width,
              r.origin.x + r.size.width
            );
            var maxY = Math.max(
              boundingRect.origin.y + boundingRect.size.height,
              r.origin.y + r.size.height
            );
            boundingRect = {
              origin: { x: minX, y: minY },
              size: { width: maxX - minX, height: maxY - minY },
            };
          }
        }

        // Create a temporary underline annotation
        var annotId = uuidV4();
        var annotObj = {
          type: PdfAnnotationSubtype.UNDERLINE,
          id: annotId,
          created: new Date(),
          flags: ["print"],
          blendMode: PdfBlendMode.Multiply,
          pageIndex: targetPageIndex,
          rect: boundingRect,
          segmentRects: matchingResult.rects,
          color: "#ff0000",
          opacity: 1,
        };

        _that.annotationPlugin.createAnnotation(targetPageIndex, annotObj);
        _that.chatCitationAnnotationId = annotId;

        // Auto-delete after 5 seconds
        _that.chatCitationTimeout = setTimeout(function () {
          _that.clearChatCitation();
        }, 5000);
      },
      function () {
        // Search failed — no highlight, viewer still usable
      }
    );
  };

  /**
   * Removes the current chat citation underline annotation, if any.
   * Uses the toggleHiddenAnnotationIds guard to prevent event handlers
   * from treating the deletion as a user action.
   */
  clearChatCitation = () => {
    if (this.chatCitationTimeout) {
      clearTimeout(this.chatCitationTimeout);
      this.chatCitationTimeout = null;
    }

    if (this.chatCitationAnnotationId && this.annotationPlugin) {
      var state = this.annotationPlugin.getState();
      if (state && state.byUid && state.byUid[this.chatCitationAnnotationId]) {
        var tracked = state.byUid[this.chatCitationAnnotationId];
        if (tracked.commitState !== "deleted") {
          this.toggleHiddenAnnotationIds[this.chatCitationAnnotationId] = true;
          this.annotationPlugin.deleteAnnotation(
            tracked.object.pageIndex,
            this.chatCitationAnnotationId
          );
        }
      }
      this.chatCitationAnnotationId = null;
    }
  };

  // ─── React Lifecycle ───────────────────────────────────────────────

  /**
   * Builds the authenticated file.pdf URL for the displayed reference.
   */
  getDocumentUrl = () => {
    var token = security.getToken();

    return (
      loadDocumentUrl() +
      "/documents/" +
      this.props.docId +
      "/file.pdf" +
      "?token=" +
      token
    );
  };

  componentDidMount = () => {
    // The reference's PDF rendition may still be generating (office
    // uploads). The viewer is not initialized until it is ready (see
    // render()).
    var preloadedDocument =
      this.props.doc && this.props.doc.id === this.props.docId
        ? this.props.doc
        : null;

    this.cancelPdfReadyWait = waitForPdfReady({
      documentId: this.props.docId,
      document: preloadedDocument,
      fileUrl: this.getDocumentUrl(),
      onReady: () => {
        this.setState({ pdfReady: true });
      },
      onTimeout: () => {
        this.setState({ pdfTimedOut: true });
      },
    });
  };

  componentWillUnmount = () => {
    if (this.zoomUnsubscribe) {
      this.zoomUnsubscribe();
      this.zoomUnsubscribe = null;
    }

    if (this.cancelPdfReadyWait) {
      this.cancelPdfReadyWait();
      this.cancelPdfReadyWait = null;
    }

    var _that = this;
    Object.keys(this.pendingMoveTimers).forEach(function (annotationId) {
      clearTimeout(_that.pendingMoveTimers[annotationId]);
    });
    this.pendingMoveTimers = {};
  };

  componentWillReceiveProps = (nextProps) => {
    if (nextProps.references[0]) {
      this.setState({
        referenceDocumentLabel:
          nextProps.references[0].document.reference_document_label,
      });
    }

    if (
      nextProps.hideOutsideAnnotations !== this.props.hideOutsideAnnotations
    ) {
      if (nextProps.hideOutsideAnnotations) {
        this.hideExternalAnnots();
      } else {
        this.showExternalAnnots();
      }
    }

    if (
      nextProps.hideReferenceAnnotations !== this.props.hideReferenceAnnotations
    ) {
      if (nextProps.hideReferenceAnnotations) {
        this.hideReferenceAnnots();
      } else {
        this.showReferenceAnnots();
      }
    }
  };

  shouldComponentUpdate = (nextProps, nextState) => {
    // The viewer manages its own updates through the plugin APIs; React
    // re-renders only when the PDF-readiness gate changes (see render()).
    return (
      nextState.pdfReady !== this.state.pdfReady ||
      nextState.pdfTimedOut !== this.state.pdfTimedOut
    );
  };

  render() {
    if (!this.state.pdfReady) {
      return (
        <div className={styles.webViewerContainer} id="webViewerContainer">
          <div className={styles.processingContainer}>
            {this.state.pdfTimedOut ? (
              <div>
                Your document could not be processed. Please refresh the page to
                try again.
              </div>
            ) : (
              <Spinner text="Processing document..." />
            )}
          </div>
        </div>
      );
    }

    var documentUrl = this.getDocumentUrl();

    return (
      <div className={styles.webViewerContainer} id="webViewerContainer">
        <div
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            zIndex: 10,
          }}
        >
          <Menu
            showRenameModal={this.props.showRenameModal}
            showDeleteModal={this.props.showDeleteModal}
            downloadDocument={this.props.downloadDocument}
          />
        </div>
        <PaperviewPDF
          style={{ width: "100%", height: "100%" }}
          onReady={this.handleViewerReady}
          config={{
            src: documentUrl,
            wasmUrl: "/pdfium.wasm",
            worker: true,
            theme: {
              preference: "light",
            },
            zoom: {
              // Open at the user's remembered zoom level
              defaultZoomLevel: loadStoredZoomLevel() || "fit-page",
            },
            disabledCategories: [
              "mode-annotate",
              "mode-redact",
              // Shapes are enabled once the viewer is ready, unless the
              // document is approved or read-only (setUpDrawnReferences)
              "mode-shapes",
              "redaction",
              "history",
              "document",
              "panel-comment",
            ],
            // Pin drop tool (reference pin)
            annotations: {
              tools: REFERENCE_PIN_TOOLS,
            },
          }}
        />
        <div
          id="referenceLabellerContainer"
          className={styles.referenceLabellerContainer}
        >
          <form
            id="referenceLabellerForm"
            className={styles.refLabelForm}
            onSubmit={this.handleSave}
          >
            <div className={styles.toolTipContainer}>
              <div className={styles.refIdTooltipBox}>
                <Tooltip type="description" subType="referenceId" />
              </div>
            </div>
            <label className={styles.refLabelFormLabel}>
              <div className={styles.labelContainer}>
                <div className={styles.refLabel}>reference id</div>
              </div>
              <div className={styles.inputContainer}>
                <input
                  className={styles.refLabelInput}
                  disabled={true}
                  placeholder="ie: Author and Year"
                  id="referenceDocumentLabelInput"
                  name="referenceDocumentLabel"
                  type="text"
                  onChange={this.handleChange}
                />
                <img
                  id="refDocLabelPencilIcon"
                  className={styles.pencil}
                  src={Pencil}
                  onClick={this.handleEditRefDocLabel}
                />
                <img
                  id="refDocLabelCheckmark"
                  className={styles.checkmark}
                  src={GreenCheckmark}
                  onClick={this.handleConfirmRefDocLabel}
                />
              </div>
            </label>
            <div className={styles.toolTipContainer}>
              <div className={styles.annotIdTooltipBox}>
                <Tooltip type="description" subType="annotationId" />
              </div>
            </div>
            <label className={styles.refLabelFormLabel}>
              <div className={styles.labelContainer}>
                <div className={styles.refLabel}>annotation id</div>
              </div>
              <div className={styles.inputContainer}>
                <input
                  className={styles.refLabelInput}
                  disabled={true}
                  id="referenceLabelInput"
                  name="referenceLabel"
                  type="text"
                  onChange={this.handleChange}
                />
                <img
                  id="refLabelPencilIcon"
                  className={styles.pencil}
                  src={Pencil}
                  onClick={this.handleEditRefLabel}
                />
                <img
                  id="refLabelCheckmark"
                  className={styles.checkmark}
                  src={GreenCheckmark}
                  onClick={this.handleConfirmRefLabel}
                />
              </div>
            </label>
            <input
              className={styles.refLabelSubmit}
              type="submit"
              value="Save"
            />
            <span
              className={styles.closeFailedReference}
              onClick={this.hideLabellerModal}
            >
              &times;
            </span>
          </form>
          <div className={styles.labelErrorContainer}>
            <div className={styles.refDocLabelErrorContainer}>
              <span id="refDocLabelError" className={styles.refDocLabelError}>
                Set a reference ID to continue
              </span>
              <span
                id="refDocLabelExistsError"
                className={styles.refDocLabelError}
              >
                This reference ID is taken
              </span>
            </div>
            <div className={styles.refLabelErrorContainer}>
              <span id="refLabelError" className={styles.refLabelError}>
                This annotation ID is taken
              </span>
              <span id="refLabelEmptyError" className={styles.refLabelError}>
                Set an annotation ID to continue
              </span>
            </div>
          </div>
          <div
            id="refDocLabelConfirmationForm"
            className={styles.confirmationForm}
          >
            <div className={styles.confirmationMessageContainer}>
              <h4 className={styles.confirmationTitle}>
                Confirm Reference ID Change
              </h4>
              <p className={styles.confirmationMessage}>
                Are your sure you want to change the Reference ID?
              </p>
            </div>
            <div className={styles.buttonContainer}>
              <UikButton
                className={styles.changeButton}
                onClick={this.closeChangeSubmission}
              >
                Cancel
              </UikButton>
              <UikButton onClick={this.handleChangeSubmission} primary>
                Change
              </UikButton>
            </div>
            <span
              className={styles.closeFailedReference}
              onClick={this.closeChangeSubmission}
            >
              &times;
            </span>
          </div>
        </div>
      </div>
    );
  }
}

export default PDFTReference;
